import { useEffect, useMemo, useRef, useState } from 'react';
import {
  DndContext,
  MouseSensor,
  TouchSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { SortableContext, rectSortingStrategy } from '@dnd-kit/sortable';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Menu,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import ShortcutTile from './ShortcutTile';
import ShortcutEditorDialog from './ShortcutEditorDialog';
import { openShortcutInBrowser } from '../../../services/shortcuts/openShortcutUrl';
import { getAskSbAccess } from '../../../services/askSb/askSbApi';
import AskSbPanel from './AskSbPanel';

function canOpenAskSbPanel(access) {
  if (!access) return false;
  if (access.canSearch === true || access.canConsume === true) return true;
  const reasons = Array.isArray(access.setupReasons) ? access.setupReasons : [];
  return access.authorized === true && !reasons.includes('drive') && reasons.includes('openai');
}

function AskSbSetup({ access }) {
  const reasons = Array.isArray(access?.setupReasons) ? access.setupReasons : [];
  const showDrive = reasons.length === 0 || reasons.includes('drive');
  const showOpenAi = reasons.length === 0 || reasons.includes('openai');
  return (
    <Box data-testid="ask-sb-setup-required" sx={{ minWidth: 0 }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
        Ask Signal Bridges
      </Typography>
      <Typography variant="body2" sx={{ mt: 1 }}>
        Setup is still required before questions can be answered.
      </Typography>
      {showDrive ? (
        <Typography variant="body2" sx={{ mt: 1 }}>
          Connect Google Drive and confirm the policy folder.
        </Typography>
      ) : null}
      {showOpenAi ? (
        <Typography variant="body2" sx={{ mt: 1 }}>
          In Integrations, select model gpt-4.1 and save the API key. The key is not shown again.
        </Typography>
      ) : null}
      {access?.canConfigure ? (
        <Button
          component="a"
          href="/admin/integrations"
          variant="contained"
          size="small"
          sx={{ mt: 1.5 }}
        >
          Open Integrations
        </Button>
      ) : (
        <Typography variant="body2" sx={{ mt: 1 }}>
          A tenant administrator needs to finish this in Integrations.
        </Typography>
      )}
    </Box>
  );
}
import { toast } from 'react-toastify';
import { SCOPE_PERSONAL, SCOPE_SHARED } from '../../../services/shortcuts/shortcutModel';
import {
  readSupportedTenantSession,
  subscribeSupportedTenantSession,
} from '../../Admin/supportedTenantsSession';
import { clampShortcutsPanelWidth } from '../../../services/shortcuts/shortcutsPanelLayout';

function TabButton({ tab, selected, onSelect }) {
  return (
    <button
      type="button"
      role="tab"
      data-testid={tab.testId}
      aria-selected={selected}
      onClick={() => onSelect(tab.id)}
      style={{
        flexShrink: 0,
        minHeight: 40,
        padding: '8px 12px',
        border: 0,
        borderBottom: selected ? '2px solid #1976d2' : '2px solid transparent',
        background: 'transparent',
        color: selected ? '#1976d2' : '#64748b',
        fontWeight: selected ? 700 : 500,
        fontSize: 14,
        cursor: 'pointer',
      }}
    >
      {tab.title}
    </button>
  );
}

export default function ShortcutsPanel({
  open,
  variant = 'docked',
  shortcuts,
  width = 350,
  onWidthChange,
  tenantId = '',
  askSbFocus = 0,
}) {
  const [supportSession, setSupportSession] = useState(() => readSupportedTenantSession());
  const [editor, setEditor] = useState(null);
  const [tabMenu, setTabMenu] = useState(null);
  const [tabPrompt, setTabPrompt] = useState(null);
  const [tabPromptTitle, setTabPromptTitle] = useState('');
  const [suppressOpen, setSuppressOpen] = useState(false);
  const [selectedTabId, setSelectedTabId] = useState(shortcuts.activeTabId);
  const [askSbAccess, setAskSbAccess] = useState(null);
  const [askSbSelected, setAskSbSelected] = useState(false);
  const resizeRef = useRef(null);
  const askSbFocusSeen = useRef(0);
  const hidePersonal = Boolean(supportSession?.tenantId);
  const tabs = hidePersonal
    ? (shortcuts.tabs || []).filter(tab => tab.scope === SCOPE_SHARED)
    : (shortcuts.tabs || []);
  const showAskSb = !hidePersonal && (
    askSbAccess?.authorized === true || askSbAccess?.canConsume === true
  );

  useEffect(() => subscribeSupportedTenantSession(() => {
    setSupportSession(readSupportedTenantSession());
  }), []);

  useEffect(() => {
    let cancelled = false;
    getAskSbAccess()
      .then((access) => {
        if (!cancelled) setAskSbAccess(access || { canConsume: false });
      })
      .catch(() => {
        if (!cancelled) setAskSbAccess({ canConsume: false });
      });
    return () => {
      cancelled = true;
    };
  }, [tenantId]);

  useEffect(() => {
    if (!showAskSb) {
      setAskSbSelected(false);
      return;
    }
    if (!askSbFocus || askSbFocus === askSbFocusSeen.current) return;
    askSbFocusSeen.current = askSbFocus;
    setAskSbSelected(true);
  }, [askSbFocus, showAskSb]);

  useEffect(() => {
    if (selectedTabId && tabs.some(tab => tab.id === selectedTabId)) return;
    setSelectedTabId(tabs[0]?.id || '');
  }, [tabs, selectedTabId]);

  const iconTenantId = tenantId || shortcuts.payload?.tenantId || '';
  const selectedId = tabs.some(tab => tab.id === selectedTabId)
    ? selectedTabId
    : tabs[0]?.id;
  const selectedTab = tabs.find(tab => tab.id === selectedId);
  const canEditSelected =
    selectedTab?.scope === SCOPE_PERSONAL ||
    (selectedTab?.scope === SCOPE_SHARED && shortcuts.canManageShared);
  const visibleShortcuts = useMemo(() => {
    const items = shortcuts.allShortcuts || shortcuts.activeShortcuts || [];
    return items.filter(item => item.tabId === selectedId);
  }, [shortcuts.allShortcuts, shortcuts.activeShortcuts, selectedId]);

  const selectTab = tabId => {
    setSelectedTabId(tabId);
    shortcuts.setActiveTabId(tabId);
  };

  const tileSensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 8 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 180, tolerance: 8 } })
  );

  const beginResize = event => {
    if (!onWidthChange) return;
    event.preventDefault();
    resizeRef.current = {
      startX: event.clientX,
      startWidth: width,
    };
    try {
      event.currentTarget.setPointerCapture?.(event.pointerId);
    } catch {
      /* jsdom */
    }
  };

  const moveResize = event => {
    if (!resizeRef.current || !onWidthChange) return;
    onWidthChange(
      clampShortcutsPanelWidth(
        resizeRef.current.startWidth + (resizeRef.current.startX - event.clientX),
        typeof window !== 'undefined' ? window.innerWidth : 1280
      )
    );
  };

  const endResize = event => {
    resizeRef.current = null;
    try {
      event.currentTarget.releasePointerCapture?.(event.pointerId);
    } catch {
      /* jsdom */
    }
  };

  if (!open) return null;

  const handleOpen = async shortcut => {
    if (suppressOpen) return;
    try {
      await openShortcutInBrowser(shortcut.url);
    } catch (err) {
      toast.error(err?.message || 'Could not open in browser');
    }
  };

  const handleShortcutDragEnd = async event => {
    const { active, over } = event;
    setTimeout(() => setSuppressOpen(false), 250);
    if (!over || active.id === over.id) return;
    try {
      await shortcuts.reorderShortcuts(active.id, over.id);
    } catch {
      // persist() already toasts
    }
  };

  return (
    <Box
      sx={{
        position: variant === 'docked' ? 'fixed' : 'relative',
        top: variant === 'docked'
          ? 'var(--sb-home-notice-bottom, var(--sb-workspace-chrome-top, 0px))'
          : 'auto',
        right: variant === 'docked' ? 0 : 'auto',
        bottom: variant === 'docked' ? 0 : 'auto',
        zIndex: variant === 'docked' ? 1450 : 'auto',
        pointerEvents: 'auto',
        width,
        maxWidth: '100%',
        height: variant === 'docked'
          ? 'calc(100vh - var(--sb-home-notice-bottom, var(--sb-workspace-chrome-top, 0px)))'
          : '100%',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: '#f8fafc',
        borderLeft: variant === 'docked' ? '1px solid' : 'none',
        borderColor: 'divider',
        boxShadow: variant === 'drawer' ? '-8px 0 24px rgba(15, 23, 42, 0.12)' : 'none',
        minWidth: 0,
      }}
      data-testid="shortcuts-panel"
      style={{ width }}
    >
      {onWidthChange ? (
        <Box
          component="button"
          type="button"
          data-testid="shortcuts-resize"
          aria-label="Resize shortcuts panel"
          onPointerDown={beginResize}
          onPointerMove={moveResize}
          onPointerUp={endResize}
          onPointerCancel={endResize}
          sx={{
            position: 'absolute',
            left: 0,
            bottom: 0,
            width: 8,
            p: 0,
            border: 0,
            bgcolor: 'transparent',
            top: 48,
            cursor: 'col-resize',
            zIndex: 1,
            '&:hover': { bgcolor: 'rgba(37, 99, 235, 0.18)' },
          }}
        />
      ) : null}
      <Box
        sx={{
          px: 1,
          pt: 0.5,
          pb: 0.5,
          borderBottom: '1px solid',
          borderColor: 'divider',
          bgcolor: '#fff',
          position: 'relative',
          zIndex: 3,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Box
            role="tablist"
            sx={{ display: 'flex', flex: 1, minWidth: 0, overflowX: 'auto', alignItems: 'stretch' }}
          >
            {showAskSb ? (
              <TabButton
                tab={{ id: 'ask-sb', title: 'Ask SB', testId: 'ask-sb-tab' }}
                selected={askSbSelected}
                onSelect={() => setAskSbSelected(true)}
              />
            ) : null}
            {tabs.map(tab => (
              <TabButton
                key={tab.id}
                tab={tab}
                selected={tab.id === selectedId && !askSbSelected}
                onSelect={(tabId) => {
                  setAskSbSelected(false);
                  selectTab(tabId);
                }}
              />
            ))}
          </Box>
          <IconButton
            size="small"
            aria-label="Tab options"
            onClick={event => setTabMenu(event.currentTarget)}
          >
            <MoreHorizIcon fontSize="small" />
          </IconButton>
        </Box>
      </Box>

      {shortcuts.sharedConflict ? (
        <Alert
          severity="warning"
          sx={{ mx: 1, mt: 1 }}
          action={
            <Button color="inherit" size="small" onClick={shortcuts.reloadShared}>
              Reload latest
            </Button>
          }
        >
          {shortcuts.sharedConflict.message}
        </Alert>
      ) : null}
      <Box sx={{
        flex: 1,
        overflow: askSbSelected ? 'hidden' : 'auto',
        px: askSbSelected ? 1.5 : 1.5,
        pt: 1.5,
        pb: askSbSelected ? 1.5 : (canEditSelected ? 9 : 1.5),
        minHeight: 0,
        minWidth: 0,
        display: 'flex',
        flexDirection: 'column',
      }}>
        {askSbSelected ? (
          canOpenAskSbPanel(askSbAccess) ? <AskSbPanel /> : <AskSbSetup access={askSbAccess} />
        ) : shortcuts.loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={24} />
          </Box>
        ) : shortcuts.error ? (
          <Alert
            severity="error"
            action={
              <Button color="inherit" size="small" onClick={shortcuts.retryLoad}>
                Retry
              </Button>
            }
          >
            {shortcuts.error}
          </Alert>
        ) : visibleShortcuts.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ px: 1, py: 2 }}>
            {canEditSelected
              ? 'No shortcuts in this tab yet. Add a link to open it in your browser.'
              : 'No tenant shortcuts in this tab yet.'}
          </Typography>
        ) : (
          <DndContext
            sensors={tileSensors}
            collisionDetection={closestCenter}
            onDragStart={() => setSuppressOpen(true)}
            onDragCancel={() => setTimeout(() => setSuppressOpen(false), 250)}
            onDragEnd={handleShortcutDragEnd}
          >
            <SortableContext
              items={visibleShortcuts.map(item => item.id)}
              strategy={rectSortingStrategy}
            >
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
                  gap: 1,
                }}
              >
                {visibleShortcuts.map(item => (
                  <ShortcutTile
                    key={item.id}
                    shortcut={item}
                    canDrag={canEditSelected}
                    canEdit={canEditSelected}
                    suppressOpen={suppressOpen}
                    tenantId={iconTenantId}
                    onOpen={handleOpen}
                    onEdit={setEditor}
                  />
                ))}
              </Box>
            </SortableContext>
          </DndContext>
        )}
      </Box>

      {shortcuts.saveError ? (
        <Alert severity="warning" sx={{ mx: 1.5, mb: 1 }}>
          {shortcuts.saveError}
        </Alert>
      ) : null}
      {!askSbSelected && !shortcuts.error && !canEditSelected ? (
        <Typography variant="caption" color="text.secondary" sx={{ px: 2, pb: 1.5 }}>
          Tenant-managed links can be opened but not changed.
        </Typography>
      ) : null}
      {!askSbSelected && !shortcuts.error && canEditSelected ? (
        <IconButton
          aria-label="Add shortcut"
          data-testid="add-shortcut"
          onClick={() => setEditor({})}
          disabled={!shortcuts.payload.tenantId && Boolean(shortcuts.error)}
          sx={{
            position: 'absolute',
            right: 16,
            bottom: 16,
            zIndex: 4,
            width: 48,
            height: 48,
            bgcolor: 'primary.main',
            color: '#fff',
            boxShadow: '0 2px 8px rgba(15, 23, 42, 0.24)',
            '&:hover': { bgcolor: 'primary.dark' },
          }}
        >
          <AddIcon />
        </IconButton>
      ) : null}

      <Menu
        anchorEl={tabMenu}
        open={Boolean(tabMenu)}
        onClose={() => setTabMenu(null)}
        sx={{ zIndex: 1600 }}
      >
        {hidePersonal ? null : (
          <MenuItem
            onClick={() => {
              setTabMenu(null);
              setTabPromptTitle('');
              setTabPrompt({ mode: 'add', scope: SCOPE_PERSONAL });
            }}
          >
            New personal tab
          </MenuItem>
        )}
        {shortcuts.canManageShared ? (
          <MenuItem
            onClick={() => {
              setTabMenu(null);
              setTabPromptTitle('');
              setTabPrompt({ mode: 'add', scope: SCOPE_SHARED });
            }}
          >
            New tenant tab
          </MenuItem>
        ) : null}
        <MenuItem
          disabled={!canEditSelected}
          onClick={() => {
            setTabMenu(null);
            setTabPromptTitle(selectedTab?.title || '');
            setTabPrompt({ mode: 'rename' });
          }}
        >
          Rename tab
        </MenuItem>
        <MenuItem
          disabled={
            !canEditSelected ||
            !tabs[tabs.findIndex(tab => tab.id === selectedId) - 1] ||
            tabs[tabs.findIndex(tab => tab.id === selectedId) - 1]
              ?.scope !== selectedTab?.scope
          }
          onClick={async () => {
            const index = tabs.findIndex(tab => tab.id === selectedId);
            try {
              await shortcuts.reorderTabs(selectedId, tabs[index - 1].id);
              setTabMenu(null);
            } catch {
              // persist toasts
            }
          }}
        >
          Move tab left
        </MenuItem>
        <MenuItem
          disabled={
            !canEditSelected ||
            !tabs[tabs.findIndex(tab => tab.id === selectedId) + 1] ||
            tabs[tabs.findIndex(tab => tab.id === selectedId) + 1]
              ?.scope !== selectedTab?.scope
          }
          onClick={async () => {
            const index = tabs.findIndex(tab => tab.id === selectedId);
            try {
              await shortcuts.reorderTabs(selectedId, tabs[index + 1].id);
              setTabMenu(null);
            } catch {
              // persist toasts
            }
          }}
        >
          Move tab right
        </MenuItem>
        <MenuItem
          disabled={!canEditSelected || selectedTab?.system}
          onClick={async () => {
            try {
              await shortcuts.deleteTab(selectedId);
              setTabMenu(null);
            } catch (err) {
              toast.error(err?.message || 'Could not delete tab');
            }
          }}
        >
          Delete tab
        </MenuItem>
      </Menu>

      <Dialog
        open={Boolean(tabPrompt)}
        onClose={() => setTabPrompt(null)}
        fullWidth
        maxWidth="xs"
        sx={{ zIndex: 1700 }}
      >
        <DialogTitle>
          {tabPrompt?.mode === 'rename'
            ? 'Rename tab'
            : tabPrompt?.scope === SCOPE_SHARED
              ? 'New tenant tab'
              : 'New personal tab'}
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            size="small"
            sx={{ mt: 1 }}
            label={tabPrompt?.scope === SCOPE_SHARED ? 'Tenant tab name' : 'Tab name'}
            value={tabPromptTitle}
            onChange={e => setTabPromptTitle(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter') {
                e.preventDefault();
                e.currentTarget.form?.requestSubmit?.();
              }
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTabPrompt(null)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={async () => {
              try {
                if (tabPrompt?.mode === 'rename') {
                  await shortcuts.renameTab(selectedId, tabPromptTitle);
                } else {
                  await shortcuts.addTab(
                    tabPromptTitle,
                    tabPrompt?.scope === SCOPE_SHARED ? SCOPE_SHARED : SCOPE_PERSONAL
                  );
                }
                setTabPrompt(null);
                setTabPromptTitle('');
              } catch {
                // persist() already toasts
              }
            }}
          >
            {tabPrompt?.mode === 'rename' ? 'Save name' : 'Add tab'}
          </Button>
        </DialogActions>
      </Dialog>

      <ShortcutEditorDialog
        open={editor !== null}
        shortcut={editor?.id ? editor : null}
        tenantId={iconTenantId}
        imageScope={selectedTab?.scope === SCOPE_SHARED ? SCOPE_SHARED : 'personal'}
        onClose={() => setEditor(null)}
        onSave={values => shortcuts.upsertShortcut({ ...values, tabId: selectedId })}
        onDelete={shortcuts.deleteShortcut}
      />
    </Box>
  );
}
